import { PlanDetailsType, createPaymentPlan } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { usePollWorkflowsHook } from '@frontend/payments-hooks';
import { Modal, useModalLoadingState, useAlert } from '@frontend/design-system';
import { createFutureISODateTimeInUTC } from '../../utils/date-helpers';
import { PaymentPlanDetailsModalBody } from './payment-plan-details-modal-body';

export const CreatePlanDetailsModal = ({
  planDetails,
  onClose,
  onComplete,
  onFailure,
}: {
  planDetails: PlanDetailsType | undefined;
  onClose: () => void;
  onComplete: () => void;
  onFailure: () => void;
}) => {
  const { t } = useTranslation('payments');
  const { setLoading } = useModalLoadingState();
  const alert = useAlert();

  const { startPolling } = usePollWorkflowsHook({
    onFailure: () => {
      onFailure();
    },
    onComplete: () => {
      onComplete();
    },
    onPollingEnd: async () => {
      onClose();
      setLoading(false);
    },
  });

  const handleSubmitPaymentPlan = async () => {
    if (!planDetails) {
      alert.error(t('Plan details is not available'));
      return;
    }
    setLoading(true, 'Creating payment plan');
    try {
      const { workflowId } = await createPaymentPlan({
        locationId: planDetails.locationId,
        name: planDetails.planName,
        amount: planDetails.planAmount,
        frequency: planDetails.frequency,
        personId: planDetails.patient?.id,
        customerEmailId: planDetails?.customerEmailId,
        paymentMethodId: planDetails?.billingMethod?.id,
        paymentTerm: Number(planDetails.paymentTerm),
        startAt: createFutureISODateTimeInUTC(planDetails.paymentDate || ''),
        ...(planDetails.invoiceId && { externalInvoiceId: planDetails.invoiceId }),
        ...(planDetails.memo && { notes: planDetails?.memo }),
      });

      startPolling([workflowId]);
    } catch (err) {
      setLoading(false);
      alert.error(t('Error creating plan, please try again.'));
    }
  };

  return (
    <>
      <Modal.Header textAlign='left'>{t('Create Payment Plan')}</Modal.Header>
      {planDetails ? (
        <PaymentPlanDetailsModalBody planDetails={planDetails} />
      ) : (
        <Modal.Body>{t('Plan details is not available')}</Modal.Body>
      )}
      <Modal.Footer
        primary={{
          onClick: handleSubmitPaymentPlan,
          label: t('Create Payment Plan'),
          disabled: !planDetails,
        }}
        secondary={{
          onClick: onClose,
          label: t('Continue Editing'),
        }}
      />
    </>
  );
};
